import { z } from 'zod';
/**
 * Primitive domain types. All app boundaries parse through these.
 */
// ISO-3166 alpha-2 country codes for Wave 1.
export const CountryCode = z.enum(['NG', 'GH', 'CD']);
// Currency codes used in Wave 1 plus USD pivot for reporting.
export const CurrencyCode = z.enum(['NGN', 'GHS', 'CDF', 'USD']);
/**
 * Pay component nature — explicit classifier for how a PayComponent
 * contributes to the tax/basis calculation.
 *
 * Until Sprint 1 this was inferred inside buildPayrollSnapshot by
 * regex-matching the label (`/housing|accommod|rent/`). That breaks
 * the moment someone writes "Residence stipend" or localizes the
 * label to French. Making the classification an explicit column
 * removes the guess and keeps the meaning in the hands of whoever
 * created the component, not the engine.
 *
 * Values:
 *   - `basic`            — core salary. Taxable, always in the basis.
 *   - `housing`          — housing/accommodation allowance. Taxable,
 *                          with country-specific rent-relief carve-outs.
 *   - `transport`        — transport/commute allowance. Taxable with
 *                          partial-exempt treatment in some countries.
 *   - `other_taxable`    — any other taxable allowance or bonus.
 *   - `other_nontaxable` — non-taxable benefit (reimbursement,
 *                          per-diem at statutory rates, etc).
 *
 * (Sprint 2: the now-redundant PayComponent.taxable boolean was
 * dropped. Anything that needs the boolean reconstructs it via
 * `isTaxableNature(nature)` below.)
 */
export const PayComponentNature = z.enum([
    'basic',
    'housing',
    'transport',
    'other_taxable',
    'other_nontaxable',
]);
/** True iff a component with this nature contributes to taxable gross. */
export function isTaxableNature(nature) {
    return nature !== 'other_nontaxable';
}
/**
 * Country-specific labels for the social-security identifier on Employee.
 *
 * Same column on the DB; different name on the FE so HR teams recognise
 * what they're being asked for. Mokoafrika expects to type a "CNSS
 * number"; an Acme-NG admin expects "PenCom PIN" (or RSA PIN); a Ghana
 * org expects "SSNIT number". Hiding all three behind generic "Social
 * security #" was the kind of polish gap that made the Mokoafrika team
 * not realize the field existed at all.
 */
const SOCIAL_SECURITY_LABEL_BY_COUNTRY = {
    NG: 'PenCom PIN',
    GH: 'SSNIT number',
    CD: 'CNSS number',
};
export function socialSecurityLabelForCountry(country) {
    return SOCIAL_SECURITY_LABEL_BY_COUNTRY[country];
}
/**
 * Country-specific labels for the tax identifier on Employee. Same
 * shape as socialSecurityLabelForCountry; different authority.
 */
const TAX_ID_LABEL_BY_COUNTRY = {
    NG: 'TIN',
    GH: 'GRA TIN',
    CD: 'NIF',
};
export function taxIdLabelForCountry(country) {
    return TAX_ID_LABEL_BY_COUNTRY[country];
}
/**
 * Country-specific labels for the social-security DEDUCTION line on
 * payslips. Distinct from `socialSecurityLabelForCountry` (which is
 * for the identifier on the Employee record). Reported by an HR
 * operator: a Nigerian payslip showing "Social security" reads
 * generic-incorrect — the actual term used in payroll documents is
 * "Pension contribution". Same logic per country.
 *
 * NG: pension is the headline statutory contribution (PenCom).
 *     NHF and ITF show in the per-scheme breakdown below.
 * GH: SSNIT is the single statutory scheme.
 * CD: CNSS is the headline scheme; INPP and ONEM show in the
 *     breakdown.
 */
const SOCIAL_SECURITY_DEDUCTION_LABEL_BY_COUNTRY = {
    NG: 'Pension contribution',
    GH: 'SSNIT',
    CD: 'CNSS',
};
export function socialSecurityDeductionLabelForCountry(country) {
    return SOCIAL_SECURITY_DEDUCTION_LABEL_BY_COUNTRY[country];
}
/**
 * Country-specific labels for the income-tax DEDUCTION line on
 * payslips. NG and GH both use PAYE (Pay-As-You-Earn). DRC uses
 * IPR (Impôt Professionnel sur le Revenu).
 *
 * Used in the payslip PDF and (eventually) the employee
 * self-service portal. Generic "Income tax" is fine internally
 * but a Mokoafrika employee scanning their payslip expects "IPR";
 * a Nigerian one expects "PAYE".
 */
const INCOME_TAX_LABEL_BY_COUNTRY = {
    NG: 'PAYE',
    GH: 'PAYE',
    CD: 'IPR',
};
export function incomeTaxLabelForCountry(country) {
    return INCOME_TAX_LABEL_BY_COUNTRY[country];
}
// Money = integer-representable decimal with explicit currency.
// Values are strings to preserve precision across JSON boundaries.
// Format: signed decimal with up to 4 fractional digits.
export const MoneyAmount = z
    .string()
    .regex(/^-?\d+(\.\d{1,4})?$/, 'Money amount must be a decimal with up to 4 fractional digits');
export const Money = z.object({
    amount: MoneyAmount,
    currency: CurrencyCode,
});
// URL-safe kebab-case slug.
export const OrgSlug = z
    .string()
    .min(3)
    .max(63)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Slug must be kebab-case, alphanumeric start/end');
// IANA timezone string (e.g. "Africa/Lagos"). Validated at app layer against IANA db.
export const Timezone = z.string().min(1);
// BCP-47 locale tag (e.g. "en-NG", "fr-CD").
export const Locale = z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/);
// ISO-8601 datetime string.
export const IsoDateTime = z.string().datetime();
/**
 * Local currency expected for each Wave 1 country. Used to validate
 * compensation currency against an employee's country of work and to
 * pre-filter currency dropdowns on the FE.
 */
const LOCAL_CURRENCY_FOR_COUNTRY = {
    NG: 'NGN',
    GH: 'GHS',
    CD: 'CDF',
};
/**
 * The "spec currency" for a country — the currency the country's
 * compliance rule spec operates in. Tax brackets, social-security
 * thresholds, minimum wage are all denominated in this currency. The
 * payroll snapshot builder converts every employee's compensation
 * into their country's spec currency before running the calculator.
 *
 * Today: spec currency == local currency for every Wave 1 country.
 * Kept as a separate export so future jurisdictions where the rule
 * spec uses a foreign currency (e.g. an Eritrea-style USD-pegged
 * basis) can diverge without rewriting every caller.
 */
export function specCurrencyForCountry(country) {
    return LOCAL_CURRENCY_FOR_COUNTRY[country];
}
/**
 * Currencies a compensation can legally be denominated in for an
 * employee working in the given country.
 *
 * Rule:
 *   - The country's local currency (NG → NGN, GH → GHS, CD → CDF), AND
 *   - USD for expat / cross-border contracts (common for international
 *     staff, NGOs, consultants paid in hard currency).
 *
 * Anything else — e.g. paying an NG employee in GHS — is almost always
 * a data-entry mistake and is rejected by the server with a 400. The
 * underlying payroll calculator still tolerates FX conversion so this
 * is a UX guard, not a correctness concern.
 *
 * If a tenant eventually needs something exotic (e.g. NG employee paid
 * in EUR for a European parent company), we'll add EUR to the allowed
 * list and let the FX snapshot handle conversion. For Sprint 0+1 the
 * local + USD pattern covers every real case.
 */
export function allowedCurrenciesForCountry(country) {
    return [LOCAL_CURRENCY_FOR_COUNTRY[country], 'USD'];
}
export function isCurrencyAllowedForCountry(country, currency) {
    return allowedCurrenciesForCountry(country).includes(currency);
}
/**
 * Currencies an organization legitimately needs FX rates between.
 *
 * The set is dynamic per-tenant:
 *   - The org's `baseCurrency` (for roll-up totals on the dashboard).
 *   - `'USD'` (the cross-tenant reporting pivot).
 *   - The local currency of every country where the org has an active
 *     employee. An NG employee at a Mokoafrika (CDF) tenant means the
 *     payroll snapshot needs to convert their compensation into NGN
 *     (the NG spec currency), and roll the resulting NGN payslip back
 *     into CDF — both legs require FX rates that involve NGN.
 *
 * The earlier "base + USD only" rule (PR #27) was correct for
 * single-country tenants but blocked multi-country teams. Mokoafrika
 * caught it on day one: hired an NG developer, payroll refused to
 * calculate because Mokoafrika couldn't even ADD a USD↔NGN rate to
 * its own table. Per-tenant isolation is unchanged — every rate still
 * lives in exactly one org's row, RLS-enforced.
 *
 * Stays a Set under the hood so duplicates collapse: a tenant with
 * five NG employees still has NGN listed once.
 */
export function allowedFxCurrenciesForOrg(baseCurrency, employeeCountryCodes = []) {
    const set = new Set([baseCurrency, 'USD']);
    for (const cc of employeeCountryCodes) {
        set.add(LOCAL_CURRENCY_FOR_COUNTRY[cc]);
    }
    return Array.from(set);
}
/**
 * FX pair shape used by the FE FX-admin form. All ordered pairs
 * (from ≠ to) over the allowed currency set. Both directions
 * explicitly — the payroll snapshot builder auto-inverts, but
 * operators want to enter whichever direction their source quotes
 * (CBN quotes USD→NGN; aggregators often quote the inverse).
 *
 * For a USD-base org with no foreign-country employees, returns `[]`.
 */
export function allowedFxPairsForOrg(baseCurrency, employeeCountryCodes = []) {
    const currencies = allowedFxCurrenciesForOrg(baseCurrency, employeeCountryCodes);
    const pairs = [];
    for (const from of currencies) {
        for (const to of currencies) {
            if (from !== to)
                pairs.push({ from, to });
        }
    }
    return pairs;
}
/**
 * Server-side gate for POST /v1/exchange-rates. Both sides of the
 * pair must be in the dynamic allowed set, and from ≠ to.
 *
 * Empty `employeeCountryCodes` falls back to the legacy "base + USD
 * only" rule — this is the right default for a brand-new tenant with
 * no employees yet.
 */
export function isFxPairAllowedForOrg(baseCurrency, from, to, employeeCountryCodes = []) {
    const allowed = new Set(allowedFxCurrenciesForOrg(baseCurrency, employeeCountryCodes));
    return allowed.has(from) && allowed.has(to) && from !== to;
}
//# sourceMappingURL=primitives.js.map